import { Button } from '@aecom-ai-engineering/react-components';
import { Dialog } from '@ark-ui/react/dialog';
import { Portal } from '@ark-ui/react/portal';
import { type Blob, convertBlobToDocument, type Finding } from '@consigli/types';
import { Info, MessageSquareShare, X } from 'lucide-react';
import { useState, type FC } from 'react';
import { useTranslation } from 'react-i18next';

import { useViewer } from '@/features/document-viewer/use-viewer';
import { useViewerContext, ViewerMode } from '@/features/document-viewer/use-viewer-context';

import { type AddContextMenuKind, type AddContextSelection } from '../types';
import { MAX_ATTACHMENTS, useAgentChatAttachments } from '../use-agent-chat-attachments';
import { AddContextDocumentsPanel } from './add-context-documents-panel';
import { AddContextFindingsPanel } from './add-context-findings-panel';
import { AddContextMilestonesPanel } from './add-context-milestones-panel';
import { AddContextRequirementsPanel } from './add-context-rfp-panels';
import './add-context-modal.css';

const ICON_STROKE_WIDTH = 1.75;

const TITLE_KEYS: Record<AddContextMenuKind, string> = {
  document: 'prop-chat.add-context-document',
  finding: 'prop-chat.add-context-finding',
  milestone: 'prop-chat.add-context-milestones-title',
  'response-requirement': 'prop-chat.add-context-response-requirements-title',
};

const SELECTED_KEYS: Record<AddContextMenuKind, string> = {
  document: 'prop-chat.add-context-documents-selected',
  finding: 'prop-chat.add-context-findings-selected',
  milestone: 'prop-chat.add-context-items-selected',
  'response-requirement': 'prop-chat.add-context-items-selected',
};

interface AddContextModalProps {
  /** Which list to show. The caller mounts this only when there is one. */
  kind: AddContextMenuKind;
  onClose: () => void;
  onSubmit: (selection: AddContextSelection) => void;
}

/*
 * Ark's Dialog rather than the design system's: that component is 640px wide
 * with its own header and footer structure, and this design is a 1024px modal
 * with a filter row, a scrolling list and a three-part footer. Overriding its
 * width, height and all three sections' padding is more fighting than
 * composing, which is the same call `agent-chat-drawer.tsx` made with Drawer.
 *
 * Portaled, and above the drawer's `z-70`: the modal is opened from inside the
 * drawer but is wider than it and covers the page. That is the opposite of the
 * add-context menu, which stays inside the drawer precisely so it does not need
 * a z-index.
 */
export const AddContextModal: FC<AddContextModalProps> = ({ kind, onClose, onSubmit }) => {
  const { t } = useTranslation();
  const { openStandaloneDocument } = useViewer();
  const { mode } = useViewerContext();
  const { attachments } = useAgentChatAttachments();
  /*
   * Seeded from the question rather than empty, so a row already attached opens
   * ticked and the dialog shows the whole of what the question carries. Read
   * once, on mount: nothing else can attach while this is open, and re-reading
   * would fight the user's own ticks.
   */
  const [selectedFindings, setSelectedFindings] = useState<Finding[]>(() => attachments.findings);
  const [selectedDocuments, setSelectedDocuments] = useState<Blob[]>(() => attachments.documents);

  // Milestones and response requirements are findings once attached.
  const isFindings = kind !== 'document';
  const selected: (Finding | Blob)[] = isFindings ? selectedFindings : selectedDocuments;
  const count = selected.length;
  /*
   * The whole cap, not what is left of it: the attached rows are part of this
   * selection now, so counting them again would halve the room at 50 attached.
   */
  const atLimit = count >= MAX_ATTACHMENTS;

  /*
   * Submitting an unchanged selection would attach exactly what is already
   * there, so the CTA has nothing to do - including the case this replaced,
   * where an empty dialog meant nothing was picked. Unticking every row is a
   * change, though, and has to stay submittable: it detaches them.
   */
  const attachedIds = new Set(
    (isFindings ? attachments.findings : attachments.documents).map((entry) => entry.id),
  );
  const isUnchanged =
    count === attachedIds.size && selected.every((entry) => attachedIds.has(entry.id));

  /*
   * Out of the way while a preview is open, rather than closed. The viewer
   * drawer sits below this modal, and its Close counts as an interaction
   * outside this content, so `closeOnInteractOutside` would dismiss the dialog
   * along with it - taking an unsubmitted selection with it.
   * Standing aside keeps that selection, so closing the preview comes back to
   * the list with the ticks intact.
   */
  const isPreviewing = mode === ViewerMode.DocumentFullscreen;

  const handleOpenChange = (open: boolean) => {
    if (open) return;

    /*
     * Nothing to reset: the dialog unmounts on close and seeds itself from the
     * question next time, so a cancelled edit is dropped by virtue of never
     * having been submitted.
     */
    onClose();
  };

  if (isPreviewing) return null;

  return (
    <Dialog.Root
      open
      onOpenChange={({ open }) => handleOpenChange(open)}
      closeOnInteractOutside
      closeOnEscape
      lazyMount
      unmountOnExit
    >
      <Portal>
        <Dialog.Backdrop className="fixed inset-0 z-80 bg-[color-mix(in_srgb,var(--ds-color-raw-black)_40%,transparent)]" />
        <Dialog.Positioner className="fixed inset-0 z-80 flex items-center justify-center p-[var(--ds-space-4)]">
          <Dialog.Content className="add-context-modal ds-body-font flex h-[min(var(--add-context-modal-height),100%)] w-full max-w-[1024px] flex-col overflow-hidden rounded-[var(--ds-radius-16)] bg-[var(--ds-color-bg-primary)]">
            <div className="flex shrink-0 items-center justify-between border-b border-solid border-[var(--ds-color-border-primary)] px-[var(--ds-space-8)] py-[var(--ds-space-4)]">
              <Dialog.Title className="min-w-0 flex-1 truncate text-[length:var(--ds-typography-heading-4-font-size)] font-[var(--ds-typography-paragraph-paragraph-medium-weight)] leading-[var(--ds-typography-heading-4-line-height)] tracking-[var(--ds-typography-heading-4-letter-spacing)] text-[var(--ds-color-text-primary)]">
                {/* The item the user picked, so the dialog names what it lists. */}
                {t(TITLE_KEYS[kind])}
              </Dialog.Title>
              <Dialog.CloseTrigger
                aria-label={t('prop-chat.add-context-close')}
                className="add-context-close flex size-[var(--ds-space-6)] shrink-0 cursor-pointer items-center justify-center text-[var(--ds-color-bg-icon-primary)]"
              >
                <X
                  aria-hidden="true"
                  strokeWidth={ICON_STROKE_WIDTH}
                  className="size-[var(--ds-space-6)]"
                />
              </Dialog.CloseTrigger>
            </div>

            <div className="flex min-h-0 flex-1 flex-col gap-[var(--ds-space-4)] border-b border-solid border-[var(--ds-color-border-primary)] px-[var(--ds-space-6)] pt-[var(--ds-space-4)]">
              {kind === 'milestone' ? (
                <AddContextMilestonesPanel
                  onSelectedChange={(findings) =>
                    setSelectedFindings(findings.slice(0, MAX_ATTACHMENTS))
                  }
                  selected={selectedFindings}
                />
              ) : kind === 'response-requirement' ? (
                <AddContextRequirementsPanel
                  onSelectedChange={(findings) =>
                    setSelectedFindings(findings.slice(0, MAX_ATTACHMENTS))
                  }
                  selected={selectedFindings}
                />
              ) : isFindings ? (
                <AddContextFindingsPanel
                  onSelectedChange={(findings) =>
                    setSelectedFindings(findings.slice(0, MAX_ATTACHMENTS))
                  }
                  selected={selectedFindings}
                />
              ) : (
                <AddContextDocumentsPanel
                  onPreview={(document) => openStandaloneDocument(convertBlobToDocument(document))}
                  onSelectedChange={(documents) =>
                    setSelectedDocuments(documents.slice(0, MAX_ATTACHMENTS))
                  }
                  selected={selectedDocuments}
                />
              )}
            </div>

            <div className="flex shrink-0 items-center justify-between px-[var(--ds-space-6)] py-[var(--ds-space-4)]">
              <div className="flex min-w-0 items-center gap-[var(--ds-space-4)]">
                <p
                  aria-live="polite"
                  className="whitespace-nowrap text-[length:var(--ds-typography-paragraph-small-font-size)] font-[var(--ds-typography-paragraph-paragraph-medium-weight)] leading-[var(--ds-typography-paragraph-small-line-height)] text-[var(--ds-color-text-primary)]"
                >
                  {t(SELECTED_KEYS[kind], { count })}
                </p>
                {/*
                 * Only at the cap, where it explains why the next row will not
                 * tick. role=status rather than sharing the count's live region,
                 * so reaching the limit is announced once in its own right.
                 */}
                {atLimit ? (
                  <p
                    role="status"
                    className="flex min-w-0 items-center gap-[var(--ds-space-2)] rounded-[var(--ds-radius-8)] bg-[var(--ds-color-bg-light-negative)] px-[var(--ds-space-4)] py-[var(--ds-space-2)] text-[length:var(--ds-typography-paragraph-13-regular-font-size)] leading-[var(--ds-typography-paragraph-13-regular-line-height)] text-[var(--ds-color-text-primary)]"
                  >
                    <Info
                      aria-hidden="true"
                      strokeWidth={ICON_STROKE_WIDTH}
                      className="size-[var(--ds-space-4)] shrink-0 text-[var(--ds-color-bg-negative)]"
                    />
                    <span className="truncate">
                      {t(
                        isFindings
                          ? 'prop-chat.add-context-findings-limit'
                          : 'prop-chat.add-context-documents-limit',
                        { max: MAX_ATTACHMENTS },
                      )}
                    </span>
                  </p>
                ) : null}
              </div>
              <div className="flex items-center gap-[var(--ds-space-2)]">
                <Button variant="outline" size="md" onClick={() => handleOpenChange(false)}>
                  {t('prop-chat.add-context-cancel')}
                </Button>
                <Button
                  variant="solid"
                  size="md"
                  disabled={isUnchanged}
                  onClick={() =>
                    onSubmit({ documents: selectedDocuments, findings: selectedFindings })
                  }
                >
                  {t('prop-chat.add-to-chat')}
                  <MessageSquareShare
                    aria-hidden="true"
                    strokeWidth={ICON_STROKE_WIDTH}
                    className="size-[var(--ds-space-4)]"
                  />
                </Button>
              </div>
            </div>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
};
